<template>
  <div class="inertial-force-viz-pro">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>$F_i = -m · a_f$</div>
    </div>

    <div class="main-layout vertical">
      <div class="simulation-stage dark-stage">
        <svg width="100%" height="240" viewBox="0 0 400 240">
          <defs>
             <linearGradient id="roadGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stop-color="#334155" />
                <stop offset="100%" stop-color="#0f172a" />
             </linearGradient>
             <!-- Perspective Floor Grid -->
             <pattern id="grid-proto" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d="M 40 0 L 0 0 0 40" fill="none" stroke="rgba(255,255,255,0.05)" stroke-width="1"/>
             </pattern>
          </defs>

          <!-- Environment -->
          <rect width="400" height="240" fill="url(#grid-proto)" />
          <line x1="0" y1="190" x2="400" y2="190" stroke="#475569" stroke-width="3" />

          <!-- The Accelerating Reference Frame (A Bus/Car) -->
          <g :transform="`translate(${carX}, 120)`">
             <!-- Reference Frame Body -->
             <rect x="-80" y="0" width="160" height="70" fill="rgba(30, 41, 59, 0.8)" stroke="#64748b" stroke-width="2" rx="4" />
             <!-- Windows -->
             <rect x="-70" y="10" width="40" height="25" fill="rgba(56, 189, 248, 0.1)" stroke="rgba(56, 189, 248, 0.3)" />
             <rect x="-20" y="10" width="40" height="25" fill="rgba(56, 189, 248, 0.1)" stroke="rgba(56, 189, 248, 0.3)" />
             <rect x="30" y="10" width="40" height="25" fill="rgba(56, 189, 248, 0.1)" stroke="rgba(56, 189, 248, 0.3)" />
             
             <!-- Wheels with dynamic rotation -->
             <g :transform="`translate(-50, 70) rotate(${carX * 2})`">
                <circle r="12" fill="#0f172a" stroke="#334155" />
                <line x1="-12" y1="0" x2="12" y2="0" stroke="#334155" />
             </g>
             <g :transform="`translate(50, 70) rotate(${carX * 2})`">
                <circle r="12" fill="#0f172a" stroke="#334155" />
                <line x1="-12" y1="0" x2="12" y2="0" stroke="#334155" />
             </g>

             <!-- The Pendulum (Physical payload inside the frame) -->
             <g transform="translate(0, 5)">
                <line x1="0" y1="0" :x2="penX" :y2="penY" stroke="#f8fafc" stroke-width="2" stroke-linecap="round" />
                <circle :cx="penX" :cy="penY" r="8" fill="#ef4444" filter="url(#glow-sf)" />
                
                <!-- Inertial Force Vector (The "Ghost" force) -->
                <g v-if="accel !== 0" :transform="`translate(${penX}, ${penY})`">
                   <line x1="0" y1="0" :x2="-accel * 30" y2="0" stroke="#fbbf24" stroke-width="3" marker-end="url(#arr-inertial-f)" />
                   <text :x="-accel * 30" y="-12" fill="#fbbf24" font-size="10" font-weight="900" text-anchor="middle">惯性力 F_i</text>
                </g>
             </g>

             <!-- Acceleration vector of the frame itself (Relative to ground) -->
             <g v-if="accel !== 0" transform="translate(0, 85)">
                <line x1="0" y1="0" :x2="accel * 30" y2="0" stroke="#38bdf8" stroke-width="3" marker-end="url(#arr-accel-f)" />
                <text :x="accel * 30" y="15" fill="#38bdf8" font-size="10" font-weight="900" text-anchor="middle">系统加速度 a</text>
             </g>
          </g>

          <defs>
             <marker id="arr-inertial-f" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
                <path d="M0,0 L6,3 L0,6 Z" fill="#fbbf24" />
             </marker>
             <marker id="arr-accel-f" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
                <path d="M0,0 L6,3 L0,6 Z" fill="#38bdf8" />
             </marker>
             <filter id="glow-sf"><feGaussianBlur stdDeviation="2" result="blur" /><feComposite in="SourceGraphic" in2="blur" operator="over" /></filter>
          </defs>
        </svg>

        <div class="hud-panel">
           <div class="hud-item cyan">
              <label>系统实时加速度 a</label>
              <span class="val">{{ accel.toFixed(2) }} <small>m/s²</small></span>
           </div>
           <div class="hud-item orange">
              <label>小球偏转角 θ</label>
              <span class="val">{{ angleDeg.toFixed(1) }} °</span>
           </div>
        </div>
      </div>

      <div class="compact-controls">
        <div class="control-card">
           <div class="control-vertical-group">
              <div class="param-row flex flex-row items-center gap-4">
                 <div class="flex-1">
                    <label style="font-size:11px;color:#64748b;font-weight:700;">调节公交车/滑草车加速度 (改变惯性力大小)</label>
                    <el-slider v-model="accel" :min="-5" :max="5" :step="0.1" size="small"></el-slider>
                 </div>
                 <div class="force-display" style="text-align:right; min-width:100px;">
                    <label style="font-size:10px;color:#94a3b8;">等效引力系数 g'</label>
                    <span class="val" style="font-size:18px; font-weight:800; color:#1e293b;">{{ Math.sqrt(9.8*9.8 + accel*accel).toFixed(2) }}</span>
                 </div>
              </div>
           </div>
        </div>
        <div class="discovery-card-compact" v-latex>
           <i class="el-icon-info"></i>
           <p><strong>真实物理计算：</strong>牛顿运动定律仅在“惯性参考系”下严格成立。当我们身处一个有加速度的系统（如加速的电梯、转弯的汽车）时，我们是处于<strong>非惯性参考系</strong>。为了在内部继续使用 <code>F = ma</code>，我们必须引入一个假想的<strong>惯性力</strong> <code>F_i = -m*a_sys</code>。你看，当车向右加速时，球向左倾斜，仿佛受到一股向左的力。这其实是由于球试图保持原有的运动状态（惯性），而车厢强制其追随加速导致的“虚幻力”表现。</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'InertialForceViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      accel: 2,
      carX: 100,
      carV: 0,
      penX: 0,
      penY: 50,
      timer: null,
      lastTime: 0
    }
  },
  computed: {
    angleDeg() {
       // tan(theta) = a / g.  g=9.8
       return Math.atan(this.accel / 9.8) * (180 / Math.PI);
    }
  },
  methods: {
    animate() {
       const now = performance.now();
       const dt = Math.min((now - this.lastTime) / 1000, 0.1);
       this.lastTime = now;

       // Reference frame physics
       this.carV += this.accel * dt;
       this.carX += this.carV * 10 * dt;
       
       // Boundary check for visual loop
       if (this.carX > 450) {
          this.carX = -100;
       } else if (this.carX < -100) {
          this.carX = 450;
       }

       // Pendulum target position (Static deflection in non-inertial frame)
       const targetRad = Math.atan(this.accel / 9.8);
       const length = 50;
       const targetX = Math.sin(targetRad) * length;
       const targetY = Math.cos(targetRad) * length;

       // Smooth transition (simulating damping)
       this.penX += (targetX - this.penX) * 0.15;
       this.penY += (targetY - this.penY) * 0.15;

       this.timer = requestAnimationFrame(this.animate);
    }
  },
  mounted() {
    this.lastTime = performance.now();
    this.animate();
  },
  beforeDestroy() {
    if(this.timer) cancelAnimationFrame(this.timer);
  }
}
</script>

<style scoped lang="scss">
.inertial-force-viz-pro { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 0 4px; .brief { font-size: 13px; color: #1e293b; font-weight: 500; } .formula-chip { background: #f0f9ff; color: #0891b2; border: 1px solid #cffafe; padding: 4px 14px; border-radius: 20px; font-weight: 700; font-family: monospace; } }
.simulation-stage { flex: 1; min-height: 240px; background: #0f172a; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden; }
.hud-panel { position: absolute; top: 12px; left: 12px; display: flex; flex-direction: column; gap: 8px; .hud-item { background: rgba(15, 23, 42, 0.82); border-radius: 8px; border: 1px solid #1e293b; padding: 6px 12px; label { display: block; font-size: 10px; color: #94a3b8; font-weight: 700; margin-bottom: 2px; } .val { font-size: 14px; color: #f8fafc; font-weight: 800; font-family: monospace; small { font-size: 10px; color: #94a3b8; font-weight: normal; } } &.cyan { border-left: 3px solid #06b6d4; } &.orange { border-left: 3px solid #f59e0b; } } }
.compact-controls { display: flex; flex-direction: column; gap: 8px; }
.control-card { background: #ffffff; padding: 12px 16px; border-radius: 12px; border: 1px solid #e2e8f0; }
.flex { display: flex; } .flex-row { flex-direction: row; } .items-center { align-items: center; } .gap-4 { gap: 1rem; } .flex-1 { flex: 1; }
.discovery-card-compact { background: #f8fafc; padding: 10px 14px; border-radius: 10px; border: 1px solid #e2e8f0; display: flex; align-items: flex-start; gap: 10px; i { color: #2563eb; font-size: 1.1rem; margin-top: 2px; } p { flex: 1; margin: 0; font-size: 12px; color: #475569; line-height: 1.5; strong { color: #1e293b; } } }
</style>
